import { ROTULO_REGIME, UFS, type IcmsUfLegalResponse, type RegimeTributario, type TributosReformaResponse } from '@triade/contracts';
import { formatarTaxa } from '@/lib/format';
import { GradeIcms } from './grade-icms';
import {
  BASE_DESPESA_ROTULO,
  INCIDENCIA_ROTULO,
  META_LUCRO_ROTULO,
  REGIME_ROTULO,
  TRIBUTO_ROTULO,
  type PerfilEntrada,
  type RegimeEntrada,
} from './tabela-form';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const CHIP = 'rounded-full bg-line-soft px-2 py-0.5 text-caption font-semibold text-ink-body';
const EFEITO = 'text-caption text-ink-muted';

/** Textos fixos do regime (genericos: os numeros vem do que foi digitado e da API, a tela nao calcula). */
const EFEITO_PRESUMIDO = 'IRPJ e CSLL saem da receita presumida: presunção × alíquota, com percentuais separados para bens e serviços.';
const EFEITO_REAL = 'Alíquota sobre o lucro. Com meta líquida de IR, a meta é dividida por (1 − alíquota); o valor exato aparece no preço da Formação.';
const EFEITO_ADICIONAL = 'Ligue quando o adicional de IRPJ se aplicar pela regra do regime.';
const AVISO_LC224 = 'lei contestada judicialmente; ligue só com orientação do contador';
const EFEITO_SIMPLES = 'CBS/IBS estão dentro do DAS.';
const EFEITO_HIBRIDO = 'CBS/IBS saem por fora; o DAS informado é sem CBS/IBS (opção semestral, a partir de 2027).';
/** Sessao 52: efetiva do Simples calculada (a tela so explica; a conta e da API). */
const EFEITO_CALCULADA = 'O cálculo usa o faturamento e a folha de 12 meses cadastrados em Empresa › Simples Nacional.';
const ROTULO_RESERVA_CALCULADA = 'DAS efetiva sem CBS/IBS (reserva quando a partilha de CBS/IBS está a confirmar)';
/** Sessao 52: DIFAL com base dupla (texto de cadastro). */
const AJUDA_DIFAL_BASE_DUPLA = 'Desligado: base única (padrão atual). Ligado: base = (valor − ICMS de origem) ÷ (1 − interna − FCP). Vale só para não contribuinte em outra UF.';

const ROTULO_REFORMA: Record<TributosReformaResponse['linhas'][number]['tributo'], string> = {
  CBS: 'CBS',
  IBS_UF: 'IBS (estadual)',
  IBS_MUN: 'IBS (municipal)',
  IS: 'Imposto Seletivo (IS)',
};

export interface AbaTributosProps {
  perfil: PerfilEntrada;
  onChange: (p: PerfilEntrada) => void;
  podeEditar: boolean;
  /** UF da empresa (padrao da UF de origem). */
  ufEmpresa: string | null;
  /** Regime da empresa (o "herdar" mostra qual e). */
  regimeEmpresa: RegimeTributario | null;
  legal: IcmsUfLegalResponse[] | undefined;
  legalErro: boolean;
  reforma: TributosReformaResponse | undefined;
  reformaErro: boolean;
  ufsComClientes?: Set<string>;
}

/** Aba Tributos da nova vigencia: cabecalho, parametros do regime, linhas de tributo, CBS/IBS/IS (so leitura) e grade de ICMS. */
export function AbaTributos(p: AbaTributosProps) {
  const { perfil, onChange, podeEditar } = p;
  const set = (parcial: Partial<PerfilEntrada>) => onChange({ ...perfil, ...parcial });
  const campo = (chave: keyof PerfilEntrada) => (e: React.ChangeEvent<HTMLInputElement>) => set({ [chave]: e.target.value } as Partial<PerfilEntrada>);
  const ufOrigem = perfil.ufOrigem || p.ufEmpresa || '';
  const legalOrigem = p.legal?.find((l) => l.uf === ufOrigem);
  const regime = perfil.regimeApuracao;
  const calculada = perfil.simplesModoEfetiva === 'CALCULADA';

  return (
    <div className="flex flex-col gap-6">
      <section aria-label="Cabeçalho dos tributos" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label htmlFor="trib-regime" className={ROTULO}>Regime</label>
          <select id="trib-regime" className={CAMPO} value={regime} disabled={!podeEditar} onChange={(e) => set({ regimeApuracao: e.target.value as RegimeEntrada })}>
            {(Object.keys(REGIME_ROTULO) as RegimeEntrada[]).map((r) => (
              <option key={r} value={r}>
                {r === 'HERDAR_DA_EMPRESA' ? `Herdar da empresa (hoje: ${p.regimeEmpresa ? ROTULO_REGIME[p.regimeEmpresa] : 'sem regime cadastrado'})` : REGIME_ROTULO[r]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="trib-meta" className={ROTULO}>Meta de lucro</label>
          <select id="trib-meta" className={CAMPO} value={perfil.metaLucro} disabled={!podeEditar} onChange={(e) => set({ metaLucro: e.target.value as PerfilEntrada['metaLucro'] })}>
            {(Object.keys(META_LUCRO_ROTULO) as PerfilEntrada['metaLucro'][]).map((m) => (
              <option key={m} value={m}>{`Meta ${META_LUCRO_ROTULO[m]}`}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="trib-uf-origem" className={ROTULO}>UF de origem</label>
          <select id="trib-uf-origem" className={CAMPO} value={perfil.ufOrigem} disabled={!podeEditar} onChange={(e) => set({ ufOrigem: e.target.value })}>
            <option value="">{p.ufEmpresa ? `Da empresa (${p.ufEmpresa})` : 'Da empresa'}</option>
            {UFS.map((uf) => (
              <option key={uf} value={uf}>{uf}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="trib-derivacao" className={ROTULO}>Preço nas outras UFs</label>
          <select id="trib-derivacao" className={CAMPO} value={perfil.derivacaoUf} disabled={!podeEditar} onChange={(e) => set({ derivacaoUf: e.target.value as PerfilEntrada['derivacaoUf'] })}>
            <option value="LIQUIDO_DENTRO">Líquido dentro da UF (padrão)</option>
            <option value="MESMO_LUCRO">Mesmo lucro em todas as UFs</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-2 text-small text-ink-body">
            <input type="checkbox" checked={perfil.difalBaseDupla} disabled={!podeEditar} aria-describedby="difal-base-dupla-ajuda" onChange={(e) => set({ difalBaseDupla: e.target.checked })} />
            DIFAL com base dupla (LC 190/2022)
          </label>
          <span id="difal-base-dupla-ajuda" className={EFEITO}>{AJUDA_DIFAL_BASE_DUPLA}</span>
        </div>
      </section>

      <section aria-label="Parâmetros do regime" className="flex flex-col gap-4">
        <h3 className="text-small font-semibold">Parâmetros do regime</h3>
        {regime === 'HERDAR_DA_EMPRESA' && <p className={EFEITO}>Usa o regime cadastrado no perfil da empresa. Escolha outro regime para ver os parâmetros.</p>}

        {regime === 'PRESUMIDO' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="pres-irpj-bens" className={ROTULO}>Presunção IRPJ · bens (%)</label>
              <input id="pres-irpj-bens" inputMode="decimal" className={CAMPO} disabled={!podeEditar} value={perfil.presuncaoIrpjBens} onChange={campo('presuncaoIrpjBens')} />
            </div>
            <div>
              <label htmlFor="pres-irpj-serv" className={ROTULO}>Presunção IRPJ · serviços (%)</label>
              <input id="pres-irpj-serv" inputMode="decimal" className={CAMPO} disabled={!podeEditar} value={perfil.presuncaoIrpjServicos} onChange={campo('presuncaoIrpjServicos')} />
            </div>
            <div>
              <label htmlFor="pres-aliq-irpj" className={ROTULO}>Alíquota do IRPJ (%)</label>
              <input id="pres-aliq-irpj" inputMode="decimal" className={CAMPO} disabled={!podeEditar} value={perfil.aliqIrpj} onChange={campo('aliqIrpj')} />
            </div>
            <div>
              <label htmlFor="pres-csll-bens" className={ROTULO}>Presunção CSLL · bens (%)</label>
              <input id="pres-csll-bens" inputMode="decimal" className={CAMPO} disabled={!podeEditar} value={perfil.presuncaoCsllBens} onChange={campo('presuncaoCsllBens')} />
            </div>
            <div>
              <label htmlFor="pres-csll-serv" className={ROTULO}>Presunção CSLL · serviços (%)</label>
              <input id="pres-csll-serv" inputMode="decimal" className={CAMPO} disabled={!podeEditar} value={perfil.presuncaoCsllServicos} onChange={campo('presuncaoCsllServicos')} />
            </div>
            <div>
              <label htmlFor="pres-aliq-csll" className={ROTULO}>Alíquota da CSLL (%)</label>
              <input id="pres-aliq-csll" inputMode="decimal" className={CAMPO} disabled={!podeEditar} value={perfil.aliqCsll} onChange={campo('aliqCsll')} />
            </div>
            <p className={`${EFEITO} sm:col-span-2`}>{EFEITO_PRESUMIDO}</p>
          </div>
        )}

        {regime === 'REAL' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="real-aliq" className={ROTULO}>Alíquota sobre o lucro (%)</label>
              <input id="real-aliq" inputMode="decimal" className={CAMPO} disabled={!podeEditar} placeholder="padrão da lei" value={perfil.aliqLucroReal} onChange={campo('aliqLucroReal')} />
            </div>
            <p className={`${EFEITO} sm:col-span-2`}>{EFEITO_REAL}</p>
          </div>
        )}

        {(regime === 'PRESUMIDO' || regime === 'REAL') && (
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-small text-ink-body">
              <input type="checkbox" checked={perfil.adicionalIrpj} disabled={!podeEditar} onChange={(e) => set({ adicionalIrpj: e.target.checked })} />
              Adicional de IRPJ
            </label>
            <span className={EFEITO}>{EFEITO_ADICIONAL}</span>
            <label className="flex items-center gap-2 text-small text-ink-body">
              <input type="checkbox" checked={perfil.acrescimoLc224} disabled={!podeEditar} aria-describedby="aviso-lc224" onChange={(e) => set({ acrescimoLc224: e.target.checked })} />
              Acréscimo LC 224
            </label>
            <span id="aviso-lc224" role="note" className="text-caption text-danger-ink">Atenção: {AVISO_LC224}.</span>
          </div>
        )}

        {(regime === 'SIMPLES' || regime === 'SIMPLES_HIBRIDO') && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="simples-modo" className={ROTULO}>Alíquota efetiva do Simples</label>
              <select id="simples-modo" className={CAMPO} value={perfil.simplesModoEfetiva} disabled={!podeEditar} onChange={(e) => set({ simplesModoEfetiva: e.target.value as PerfilEntrada['simplesModoEfetiva'] })}>
                <option value="DIGITADA">Digitada</option>
                <option value="CALCULADA">Calculada pela receita dos últimos 12 meses</option>
              </select>
            </div>
            <div>
              <label htmlFor="simples-anexo-bem" className={ROTULO}>Anexo dos bens</label>
              <select id="simples-anexo-bem" className={CAMPO} value={perfil.simplesAnexoBem} disabled={!podeEditar} onChange={(e) => set({ simplesAnexoBem: e.target.value as PerfilEntrada['simplesAnexoBem'] })}>
                <option value="I">I (comércio)</option>
                <option value="II">II (indústria)</option>
              </select>
            </div>
            <div>
              <label htmlFor="simples-anexo-servico" className={ROTULO}>Anexo dos serviços</label>
              <select id="simples-anexo-servico" className={CAMPO} value={perfil.simplesAnexoServico} disabled={!podeEditar} onChange={(e) => set({ simplesAnexoServico: e.target.value as PerfilEntrada['simplesAnexoServico'] })}>
                <option value="III">III</option>
                <option value="V">V</option>
                <option value="FATOR_R">Fator R</option>
              </select>
            </div>
            {calculada && <p className={`${EFEITO} sm:col-span-2`}>{EFEITO_CALCULADA}</p>}

            {regime === 'SIMPLES' && !calculada && (
              <div>
                <label htmlFor="simples-das" className={ROTULO}>DAS efetivo (%)</label>
                <input id="simples-das" inputMode="decimal" className={CAMPO} disabled={!podeEditar} value={perfil.dasEfetiva} onChange={campo('dasEfetiva')} />
              </div>
            )}
            {regime === 'SIMPLES' && <p className={`${EFEITO} sm:col-span-2`}>{EFEITO_SIMPLES}</p>}

            {regime === 'SIMPLES_HIBRIDO' && (
              <div>
                <label htmlFor="hibrido-das" className={ROTULO}>{calculada ? ROTULO_RESERVA_CALCULADA : 'DAS efetivo sem CBS/IBS (%)'}</label>
                <input id="hibrido-das" inputMode="decimal" className={CAMPO} disabled={!podeEditar} value={perfil.dasEfetivaSemCbsIbs} onChange={campo('dasEfetivaSemCbsIbs')} />
              </div>
            )}
            {regime === 'SIMPLES_HIBRIDO' && !calculada && <p className={`${EFEITO} sm:col-span-2`} role="note">{EFEITO_HIBRIDO}</p>}
          </div>
        )}
      </section>

      <section aria-label="Linhas de tributo" className="flex flex-col gap-2">
        <h3 className="text-small font-semibold">Tributos</h3>
        <table className="w-full text-small" aria-label="Linhas de tributo">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="py-1 font-medium">Tributo</th>
              <th scope="col" className="py-1 font-medium">Alíquota (%)</th>
              <th scope="col" className="py-1 font-medium">Incidência</th>
              <th scope="col" className="py-1 font-medium">Base</th>
              <th scope="col" className="py-1 font-medium">Ativo</th>
            </tr>
          </thead>
          <tbody>
            {perfil.tributos.map((l, i) => {
              const rotulo = TRIBUTO_ROTULO[l.tributo] ?? l.tributo;
              return (
                <tr key={l.tributo} className="border-t border-line-soft">
                  <td className="py-1.5 pr-2">{rotulo}</td>
                  <td className="w-40 py-1.5 pr-2">
                    <input
                      aria-label={`Alíquota ${rotulo}`}
                      inputMode="decimal"
                      className={CAMPO}
                      disabled={!podeEditar}
                      placeholder={l.tributo === 'ICMS' && legalOrigem ? `legal: ${formatarTaxa(legalOrigem.interna)}` : 'padrão'}
                      value={l.aliquota}
                      onChange={(e) => set({ tributos: perfil.tributos.map((x, k) => (k === i ? { ...x, aliquota: e.target.value } : x)) })}
                    />
                  </td>
                  <td className="w-44 py-1.5 pr-2">
                    <select
                      aria-label={`Incidência ${rotulo}`}
                      className={CAMPO}
                      disabled={!podeEditar}
                      value={l.incidencia}
                      onChange={(e) => set({ tributos: perfil.tributos.map((x, k) => (k === i ? { ...x, incidencia: e.target.value as PerfilEntrada['tributos'][number]['incidencia'] } : x)) })}
                    >
                      {(Object.keys(INCIDENCIA_ROTULO) as Array<keyof typeof INCIDENCIA_ROTULO>).map((k) => (
                        <option key={k} value={k}>{INCIDENCIA_ROTULO[k]}</option>
                      ))}
                    </select>
                  </td>
                  <td className="w-40 py-1.5 pr-2 text-ink-secondary">{BASE_DESPESA_ROTULO[l.base]}</td>
                  <td className="py-1.5">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" aria-label={`Ativo ${rotulo}`} checked={l.ativo} disabled={!podeEditar} onChange={(e) => set({ tributos: perfil.tributos.map((x, k) => (k === i ? { ...x, ativo: e.target.checked } : x)) })} />
                      <span className={CHIP}>{l.ativo ? 'ligado' : 'desligado'}</span>
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className={EFEITO}>IRPJ, CSLL e DAS: preencha na linha ou nos parâmetros do regime, nunca nos dois.</p>
      </section>

      <section aria-label="CBS, IBS e IS (somente leitura)" className="flex flex-col gap-2">
        <h3 className="text-small font-semibold">CBS, IBS e IS</h3>
        {p.reformaErro && !p.reforma ? (
          <p role="status" className="text-small text-ink-muted">Não foi possível carregar CBS, IBS e IS nesta data.</p>
        ) : !p.reforma ? (
          <p role="status" className="text-small text-ink-muted">Carregando CBS, IBS e IS…</p>
        ) : (
          <table className="w-full text-small" aria-label="CBS, IBS e IS">
            <thead>
              <tr className="text-left text-caption text-ink-muted">
                <th scope="col" className="py-1 font-medium">Tributo</th>
                <th scope="col" className="py-1 font-medium">Alíquota</th>
                <th scope="col" className="py-1 font-medium">Incidência</th>
                <th scope="col" className="py-1 font-medium">Fonte</th>
              </tr>
            </thead>
            <tbody>
              {p.reforma.linhas.map((l) => (
                <tr key={l.tributo} className="border-t border-line-soft">
                  <td className="py-1.5">{ROTULO_REFORMA[l.tributo]}</td>
                  <td className="tabular py-1.5">{formatarTaxa(l.aliquota)}</td>
                  <td className="py-1.5">por fora</td>
                  <td className="py-1.5 text-ink-secondary">
                    {l.fonte}
                    {l.informativo && <span className={`${CHIP} ml-2`}>informativo</span>}
                    {l.estimada && <span className={`${CHIP} ml-2`}>estimada</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className={EFEITO} role="note">
          CBS, IBS e IS vêm da reforma, com incidência por fora. Não são editados aqui.
        </p>
      </section>

      <section aria-label="ICMS por UF" className="flex flex-col gap-2">
        <h3 className="text-small font-semibold">ICMS por UF de destino</h3>
        <GradeIcms
          perfil={perfil}
          onChange={onChange}
          podeEditar={podeEditar}
          legal={p.legal}
          legalErro={p.legalErro}
          ufsComClientes={p.ufsComClientes}
        />
      </section>
    </div>
  );
}
